import "./Details.css";

import { OrganisationTypeConstants } from "../../../../../../constants";
import { AssetManagerSingleCardTemplate, DeveloperSingleCardTemplate } from "../../../../../../templates";
import { useAuth } from "../../../../../../useAuth";
import {
  AssignResponsibleOwnerModal,
  ErrorBox,
  LoadingSpinner,
  ProjectAccessEditFields,
} from "../../../../../../widget";
import { ResendAccessInvitationModal, RevokeUserAccessModal } from "./components";
import { useDetails } from "./useDetails";

export const Details = (): JSX.Element => {
  const { currentUserType } = useAuth();
  const {
    handleBackClick,
    backLinkText,
    invitationDetails,
    organisationType,
    dateSent,
    accessOptions,
    accessSelection,
    onAccessSelectionChange,
    onSubmit,
    onCancel,
    onResendInvitation,
    showResendInvitationModal,
    onOpenResendInvitationModal,
    onCloseResendInvitationModal,
    onRevokeAccess,
    showRevokeModal,
    onOpenRevokeAccessModal,
    onCloseRevokeModal,
    showAssignResponsibleOwnerModal,
    currentResponsibleOwnerName,
    onConfirmAssignResponsibleOwner,
    onCancelAssignResponsibleOwner,
    canManageAccess,
    canRevoke,
    canResend,
    showMakeResponsibleOwner,
    isOnSubmitLoading,
    isLoading,
    hasLoadError,
    errors,
  } = useDetails();
  const isAssetManager = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;
  const content = invitationDetails && (
    <>
      <ProjectAccessEditFields
        organisationName={invitationDetails.targetOrganisation.displayName ?? ""}
        organisationType={organisationType}
        email={invitationDetails.targetOrganisation.contactEmail ?? ""}
        invitedBy={invitationDetails.sentByUser?.fullName ?? ""}
        phoneNumber={invitationDetails.targetOrganisation.contactPhone ?? ""}
        dateSent={dateSent}
        accessOptions={accessOptions}
        value={accessSelection}
        errors={errors}
        onChange={onAccessSelectionChange}
        onSubmit={onSubmit}
        onCancel={onCancel}
        showMakeResponsibleOwner={showMakeResponsibleOwner}
        canRevoke={canRevoke}
        onRevoke={onOpenRevokeAccessModal}
        canResend={canResend}
        onResend={onOpenResendInvitationModal}
        isReadOnly={!canManageAccess}
        isLoading={isOnSubmitLoading}
        corners={isAssetManager ? "rounded" : "default"}
      />
      <RevokeUserAccessModal show={showRevokeModal} onRevokeAccess={onRevokeAccess} closeModal={onCloseRevokeModal} />
      <ResendAccessInvitationModal
        show={showResendInvitationModal}
        onResendAccessInvitation={onResendInvitation}
        closeModal={onCloseResendInvitationModal}
      />
      <AssignResponsibleOwnerModal
        show={showAssignResponsibleOwnerModal}
        newOwnerName={invitationDetails.targetOrganisation.displayName ?? undefined}
        currentOwnerName={currentResponsibleOwnerName}
        onConfirm={onConfirmAssignResponsibleOwner}
        onCancel={onCancelAssignResponsibleOwner}
        isLoading={isOnSubmitLoading}
      />
    </>
  );

  const body = (
    <>
      {isLoading && (
        <div className="ProjectAccessDetails_status">
          <LoadingSpinner size="medium" type="dark" />
        </div>
      )}
      {!isLoading && hasLoadError && (
        <ErrorBox title="This invitation could not be loaded" text="Please refresh the page or try again later." />
      )}
      {!isLoading && !hasLoadError && content}
    </>
  );

  // The invitation may have failed to load, but the chrome still has to render - otherwise the page
  // is a blank screen with no back button.
  const pageTitle = invitationDetails?.project.displayName ?? "Project access";

  return (
    <div>
      {isAssetManager ? (
        <AssetManagerSingleCardTemplate
          pageTitle={pageTitle}
          handleBackClick={backLinkText ? handleBackClick : undefined}
          activeElement="Projects"
          backButtonText={backLinkText}
        >
          {body}
        </AssetManagerSingleCardTemplate>
      ) : (
        <DeveloperSingleCardTemplate
          pageTitle={pageTitle}
          handleBackClick={backLinkText ? handleBackClick : undefined}
          activeElement="Projects"
          backButtonText={backLinkText}
        >
          {body}
        </DeveloperSingleCardTemplate>
      )}
    </div>
  );
};
